import React, { createContext, useContext, useState, ReactNode } from 'react';

export interface Category {
  id: number;
  name: string;
  description: string;
  productCount: number;
  status: 'active' | 'draft';
  color: string;
  icon: string;
}

interface CategoriesContextType {
  categories: Category[];
  addCategory: (category: Omit<Category, 'id'>) => void;
  updateCategory: (id: number, updates: Partial<Category>) => void;
  deleteCategory: (id: number) => void;
  toggleCategoryStatus: (id: number) => void;
  getCategoryNames: () => string[];
}

const CategoriesContext = createContext<CategoriesContextType | undefined>(undefined);

export const useCategories = () => {
  const context = useContext(CategoriesContext);
  if (context === undefined) {
    throw new Error('useCategories must be used within a CategoriesProvider');
  }
  return context;
};

interface CategoriesProviderProps {
  children: ReactNode;
}

export const CategoriesProvider: React.FC<CategoriesProviderProps> = ({ children }) => {
  const [categories, setCategories] = useState<Category[]>([
    {
      id: 1,
      name: "Kahvaltı",
      description: "Sabah menüsü ve kahvaltılık ürünler",
      productCount: 8,
      status: "active",
      color: "orange",
      icon: "☕"
    },
    {
      id: 2,
      name: "Ana Yemekler",
      description: "Öğle ve akşam yemekleri",
      productCount: 15,
      status: "active",
      color: "blue",
      icon: "🍽️"
    },
    {
      id: 3,
      name: "Tatlılar",
      description: "Dessert ve tatlı çeşitleri",
      productCount: 6,
      status: "draft",
      color: "pink",
      icon: "🍰"
    },
    {
      id: 4,
      name: "İçecekler",
      description: "Sıcak ve soğuk içecekler",
      productCount: 12,
      status: "active",
      color: "green",
      icon: "🥤"
    }
  ]);

  const addCategory = (categoryData: Omit<Category, 'id'>) => {
    const newCategory: Category = {
      ...categoryData,
      id: Math.max(...categories.map(c => c.id), 0) + 1
    };
    setCategories(prev => [...prev, newCategory]);
  };

  const updateCategory = (id: number, updates: Partial<Category>) => {
    setCategories(prev => 
      prev.map(category => 
        category.id === id ? { ...category, ...updates } : category
      )
    );
  };

  const deleteCategory = (id: number) => {
    setCategories(prev => prev.filter(category => category.id !== id));
  };

  const toggleCategoryStatus = (id: number) => {
    setCategories(prev => 
      prev.map(category => 
        category.id === id 
          ? { ...category, status: category.status === 'active' ? 'draft' : 'active' }
          : category
      )
    );
  };

  const getCategoryNames = (): string[] => {
    return categories
      .filter(category => category.status === 'active')
      .map(category => category.name);
  };

  const value: CategoriesContextType = {
    categories,
    addCategory,
    updateCategory,
    deleteCategory,
    toggleCategoryStatus,
    getCategoryNames
  };

  return (
    <CategoriesContext.Provider value={value}>
      {children}
    </CategoriesContext.Provider>
  );
};



